import { createStyles, makeStyles, Theme } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { Auth } from 'aws-amplify';
import { some } from 'lodash';
import React from 'react';

import { AppSessionData } from 'src/constants/dataTypes/sessionDataTypes';
import { PortalConfigContext, RouteContext } from 'src/context';
import history from 'src/history';
import Button from 'src/legacy/components/Button';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BrandAvatar } from 'src/legacy/components/UI/Avatars/BrandAvatar';
import { useGetCloneableStatusQuery } from 'src/services/api';
import { GraySmall, NonHoverBorder } from 'src/theme/colors';

import { CloneWorkspaceSubTitle } from './CloneWorkspaceSubTitle';
import { OnboardingFormCard } from './OnboardingFormCard';
import { OnboardingFormCardTitle } from './OnboardingFormCardTitle';
import OnboardingFormLayout from './OnboardingForms/OnboardingFormLayout';
import { OnboardingPageLayout } from './OnboardingPageLayout';
import { OnboardingStep } from './onboardingTypes';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {
      textAlign: 'center',
      marginBottom: '40px',
    },
    listItemButton: {
      height: 48,
      border: `1px ${NonHoverBorder} solid`,
    },
    emailsList: {
      display: 'flex',
      gap: 28,
      flexDirection: 'column',
    },
    continueWithText: {
      color: GraySmall,
      marginBottom: theme.spacing(3.5),
    },
  }),
);

type EmailToUserIDMap = { [email: string]: string };

export const GetStartedWithOtherPortalPage: React.FC = () => {
  const classes = useStyles();
  const [isSessionLoading, setIsSessionLoading] = React.useState(true);
  const [userEmails, setUserEmails] = React.useState<EmailToUserIDMap>({});
  const portalConfig = React.useContext(PortalConfigContext);
  const { sessionData, query } = React.useContext(RouteContext);
  // clone workspace query params
  const { template_id: templateId, template_name: templateName } = query as {
    template_id: string;
    template_name: string;
  };

  // clone workspace query params
  const {
    template_id: workspaceTemplateId,
    template_name: workspaceTemplateName,
  } = query;
  const getUserEmails = async () => {
    try {
      setIsSessionLoading(true);
      // retrieve user emails from portal sessions
      const { portalIdToSession } = sessionData;
      const { workspace_login, ...portalSessions } = portalIdToSession;

      const appSessions: AppSessionData[] = Object.values(portalSessions);

      // add workspace_login to appSessions if the email is not a duplicate
      const isWorkspaceLoginDuplicate = some(
        appSessions,
        (session) => session.email === workspace_login.email,
      );
      if (!isWorkspaceLoginDuplicate) {
        appSessions.push(workspace_login);
      }

      // convert map of portal session into map of user emails to userIds
      const emailMap = appSessions.reduce((emailToUserId, session) => {
        const { email, userId } = session;
        const updatedEmailToUserId = { ...emailToUserId };
        if (email && userId) {
          updatedEmailToUserId[email] = userId;
        }
        return updatedEmailToUserId;
      }, {} as EmailToUserIDMap);

      setUserEmails(emailMap);
    } catch (error) {
      console.error(error);
    } finally {
      setIsSessionLoading(false);
    }
  };

  React.useEffect(() => {
    getUserEmails();
  }, []);

  /**
   * This function handle the navigation to the next onboarding step
   * it propagates the workspace template id and name if they exist
   * @param stepPath the path of the next onboarding step
   */
  const goToNextOnboardingStep = (stepPath: string) => {
    let path = stepPath;
    // preserve workspace query params if they exist
    if (workspaceTemplateId) {
      const separator = path.includes('?') ? '&' : '?';
      path += `${separator}template_id=${workspaceTemplateId}${
        workspaceTemplateName ? `&template_name=${workspaceTemplateName}` : ''
      }`;
    }

    history.push(path);
  };

  /**
   * This function will be called when user choose an email item
   */
  const handleSelectEmail = async (
    selectedEmail: string,
    selectedUserId: string,
  ) => {
    // to check if the current user is valid we need to do use cognito and call
    // Auth.getCurrentUserSession() to get the current user session
    // and also implicitly use the refresh token to refresh the access token.
    // Because our users can have multiple emails and different sessions, we need to tell cognito
    // who the current user is before calling Auth.getCurrentUserSession().
    // In order to so, get the current userId associated with the email (for us userId = email)
    // Set the userID in localStorage.LastAuthUser = userId
    window.localStorage.setItem(
      `CognitoIdentityServiceProvider.${portalConfig.AWS.Auth.userPoolWebClientId}.LastAuthUser`,
      selectedUserId,
    );

    try {
      const session = await Auth.currentSession();
      const user = await Auth.currentAuthenticatedUser();
      const { given_name, family_name } = user.attributes;
      const userFullName = `${given_name} ${family_name}`;
      // when current session is valid, redirect to setup company onboarding step
      const path = `/signup?step=${
        OnboardingStep.CompanySetup
      }&prefill_email=${encodeURIComponent(
        selectedEmail,
      )}&name=${userFullName}`;

      goToNextOnboardingStep(path);
      console.info('current session found', session);
    } catch (e) {
      console.error('current session not found', e);
      // otherwise, redirect to login page
      // in order to re-authenticate the user with the selected email
      window.location.replace(
        `/login?email=${selectedEmail}&nextOnboardingStep=${OnboardingStep.CompanySetup}`,
      );
    }
  };

  const { data: cloneableStatusData } = useGetCloneableStatusQuery(templateId, {
    skip: !templateId,
  });

  const showCloningText = Boolean(
    cloneableStatusData?.isCloneable && templateName,
  );

  return (
    <OnboardingPageLayout>
      <OnboardingFormLayout>
        <OnboardingFormCard>
          <BrandAvatar />
          <OnboardingFormCardTitle title="Select an email to continue">
            {showCloningText && (
              <CloneWorkspaceSubTitle templateName={templateName} />
            )}
          </OnboardingFormCardTitle>
          <BaseTypography
            fontType="13Medium"
            className={classes.continueWithText}
          >
            Continue with...
          </BaseTypography>
          <div className={classes.emailsList} data-testid="existing-users-list">
            {isSessionLoading && (
              <Skeleton variant="rect" className={classes.listItemButton} />
            )}
            {!isSessionLoading &&
              Object.entries(userEmails).map(([userEmail, userId]) => (
                <Button
                  key={userEmail}
                  variant="outlined"
                  htmlId={userEmail}
                  fullWidth
                  className={classes.listItemButton}
                  onClick={() => handleSelectEmail(userEmail, userId)}
                >
                  <BaseTypography fontType="15Medium">
                    {userEmail}
                  </BaseTypography>
                </Button>
              ))}
            <Button
              variant="outlined"
              htmlId="use-different-email-btn"
              data-testid="use-different-email-btn"
              fullWidth
              className={classes.listItemButton}
              onClick={() =>
                goToNextOnboardingStep(
                  `/signup?step=${OnboardingStep.CreateAccount}`,
                )
              }
            >
              <BaseTypography fontType="15Medium">
                Use a different email
              </BaseTypography>
            </Button>
          </div>
        </OnboardingFormCard>
      </OnboardingFormLayout>
    </OnboardingPageLayout>
  );
};
